import { useEffect, useMemo, useState } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "../lib/api";
import type { Id } from "../../convex/_generated/dataModel";
import { caughtErrorMessage } from "../domain/errors";
import { Button, Card, Icon, JurisBadge, cx } from "../components/ui";
import {
  digestSendNotice,
  emailUpdateChangeCountText,
  emailUpdateChangeTypeLabel,
  emailUpdateDigestButtonView,
  emailUpdateNoticeToneClass,
  emptyEmailUpdateSendWorkflowState,
  emailUpdateRecipientCountText,
  emailUpdateRecipientToneClass,
  emailUpdateTestButtonView,
  nextEmailUpdateSendWorkflowState,
  testEmailNotice,
} from "../domain/emailUpdates";
import { formatAuDate, formatAuDateTime } from "../domain/time";

export function EmailUpdates() {
  const pending = useQuery(api.emailUpdates.pending, {});
  const deliveryHistory = useQuery(api.emailUpdates.deliveryHistory, { limit: 30 });
  const send = useAction(api.emailUpdates.send);
  const sendTest = useAction(api.emailUpdates.sendTest);
  const [sendWorkflow, setSendWorkflow] = useState(() => emptyEmailUpdateSendWorkflowState());
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<Id<"companies">>>(new Set());
  const [confirmingSend, setConfirmingSend] = useState(false);
  const sending = sendWorkflow.sending;
  const testSending = sendWorkflow.testSending;
  const result = sendWorkflow.notice;

  const companies = useMemo(() => pending?.companies ?? [], [pending]);
  useEffect(() => {
    const pendingCompanies = pending?.companies ?? [];
    setSelectedCompanyIds(
      new Set(pendingCompanies.filter((row) => row.recipientCount > 0).map((row) => row.companyId)),
    );
    setConfirmingSend(false);
  }, [pending]);

  const selectedCompanies = useMemo(
    () => companies.filter((row) => selectedCompanyIds.has(row.companyId)),
    [companies, selectedCompanyIds],
  );
  const selectedEntryIds = useMemo(
    () => new Set(selectedCompanies.flatMap((row) => row.entryIds)),
    [selectedCompanies],
  );
  const selectedRecipientCount = selectedCompanies.reduce((sum, row) => sum + row.recipientCount, 0);
  const testButton = emailUpdateTestButtonView({ sending, testSending });
  const digestButton = emailUpdateDigestButtonView({
    sending,
    testSending,
    companyCount: selectedCompanies.length,
    entryCount: selectedEntryIds.size,
  });

  const onSend = async () => {
    if (selectedCompanyIds.size === 0) return;
    setSendWorkflow((current) => nextEmailUpdateSendWorkflowState(current, { type: "digestStarted" }));
    try {
      const r = await send({ companyIds: [...selectedCompanyIds] });
      setConfirmingSend(false);
      setSendWorkflow((current) => nextEmailUpdateSendWorkflowState(current, {
        type: "sendFinished",
        notice: digestSendNotice(r),
      }));
    } catch (e) {
      setSendWorkflow((current) => nextEmailUpdateSendWorkflowState(current, {
        type: "sendFailed",
        message: caughtErrorMessage(e, "Could not send email updates."),
      }));
    }
  };

  const onSendTest = async () => {
    setSendWorkflow((current) => nextEmailUpdateSendWorkflowState(current, { type: "testStarted" }));
    try {
      const r = await sendTest({});
      setSendWorkflow((current) => nextEmailUpdateSendWorkflowState(current, {
        type: "sendFinished",
        notice: testEmailNotice(r),
      }));
    } catch (e) {
      setSendWorkflow((current) => nextEmailUpdateSendWorkflowState(current, {
        type: "sendFailed",
        message: caughtErrorMessage(e, "Could not send a test email."),
      }));
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 py-7 lg:px-9">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-slate-900">Email updates</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Review each client, exact recipients and the rendered message before sending. Failed deliveries remain
            available to retry.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" icon="bell" disabled={testButton.disabled} onClick={() => void onSendTest()}>
            {testButton.label}
          </Button>
          <Button
            icon="bell"
            disabled={digestButton.disabled}
            onClick={() => setConfirmingSend(true)}
          >
            {digestButton.label}
          </Button>
        </div>
      </div>

      {result !== null && (
        <div
          className={cx("mb-5 rounded-lg border px-4 py-3 text-sm", emailUpdateNoticeToneClass(result.tone))}
        >
          {result.text}
        </div>
      )}

      {confirmingSend && (
        <Card className="mb-5 border-amber-200 bg-amber-50 px-5 py-4">
          <p className="text-sm font-semibold text-amber-950">Confirm client email delivery</p>
          <p className="mt-1 text-sm text-amber-900">
            Send {selectedEntryIds.size} pending changes to {selectedRecipientCount} recipients across{" "}
            {selectedCompanies.length} selected clients? Each client receives only changes covered by its
            subscriptions.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={sending} onClick={() => void onSend()}>
              {sending ? "Sending..." : "Confirm and send"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={sending}
              onClick={() => setConfirmingSend(false)}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {pending === undefined ? (
        <p className="text-slate-500">Loading…</p>
      ) : companies.length === 0 ? (
        <Card className="px-5 py-12 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-400"><Icon name="check" size={22} /></div>
          <p className="text-sm text-slate-500">No pending updates — all subscribed clients are up to date.</p>
        </Card>
      ) : (
        <div className="space-y-5">
          {companies.map((row) => (
            <Card key={row.companyId} className="overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedCompanyIds.has(row.companyId)}
                    disabled={row.recipientCount === 0 || sending}
                    onChange={(event) => {
                      setSelectedCompanyIds((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(row.companyId);
                        else next.delete(row.companyId);
                        return next;
                      });
                      setConfirmingSend(false);
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-[var(--accent)] focus:ring-[var(--accent)]"
                  />
                  <span className="text-[14px] font-semibold text-slate-800">{row.companyName}</span>
                </label>
                <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
                  <span className="text-slate-400">{emailUpdateChangeCountText(row.digest.totalChanges)}</span>
                  <span className={cx("rounded px-2 py-0.5 ring-1", emailUpdateRecipientToneClass(row.recipientCount))}>
                    {emailUpdateRecipientCountText(row.recipientCount)}
                  </span>
                </div>
              </div>
              <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Recipients</p>
                {row.recipientEmails.length === 0 ? (
                  <p className="mt-1 text-[12.5px] text-amber-700">
                    No active client recipients. Add or activate a client user before sending.
                  </p>
                ) : (
                  <ul className="mt-1.5 flex flex-wrap gap-1.5" aria-label={`Recipients for ${row.companyName}`}>
                    {row.recipientEmails.map((email) => (
                      <li key={email} className="rounded bg-white px-2 py-1 text-[12px] text-slate-700 ring-1 ring-slate-200">
                        {email}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="space-y-4 px-5 py-4">
                {row.digest.groups.map((g, gi) => (
                  <div key={gi}>
                    <p className="text-[13.5px] text-slate-700">
                      The <span className="font-semibold">{g.amendingLegislation}</span> has amended the following with effect from{" "}
                      <span className="font-medium">{formatAuDate(g.commencementDate)}</span>:
                    </p>
                    <ul className="mt-1.5 space-y-1">
                      {g.items.map((it, ii) => (
                        <li key={ii} className="flex items-start gap-2 text-[13px]">
                          <JurisBadge code={it.jurisdiction} />
                          <span className="text-slate-700">
                            <span className="font-medium">{it.legislationTitle}</span>{" "}
                            <span className="text-slate-400">— {emailUpdateChangeTypeLabel(it.changeType)}:</span>{" "}
                            {it.sectionLabels.join("; ") || "—"}
                            {it.affectedObligationSetNames.length > 0 && (
                              <span className="mt-1 block text-[12.5px] text-slate-500">
                                Saved obligation sets affected: {it.affectedObligationSetNames.join(", ")}
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <details className="border-t border-slate-100">
                <summary className="cursor-pointer px-5 py-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50">
                  Preview exact email
                </summary>
                <div className="border-t border-slate-100 bg-slate-100 p-3">
                  <iframe
                    title={`Email preview for ${row.companyName}`}
                    srcDoc={row.previewHtml}
                    sandbox=""
                    className="h-[420px] w-full rounded bg-white"
                  />
                </div>
              </details>
            </Card>
          ))}
        </div>
      )}

      <section className="mt-8" aria-labelledby="delivery-history-heading">
        <div className="mb-3">
          <h2 id="delivery-history-heading" className="text-base font-semibold text-slate-900">
            Recent delivery attempts
          </h2>
          <p className="mt-0.5 text-[12.5px] text-slate-500">
            Failed and skipped items stay in the pending list until a delivery succeeds.
          </p>
        </div>
        {deliveryHistory === undefined ? (
          <p className="text-sm text-slate-500">Loading delivery history…</p>
        ) : deliveryHistory.length === 0 ? (
          <Card className="px-5 py-6 text-sm text-slate-500">No delivery attempts yet.</Card>
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {deliveryHistory.map((delivery) => (
                <li key={delivery._id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-3.5">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-medium text-slate-800">
                      {delivery.company?.name ?? "Deleted company"} ·{" "}
                      {delivery.legislation?.title ?? "Deleted legislation"}
                    </p>
                    <p className="mt-0.5 text-[12px] text-slate-500">
                      {formatAuDateTime(delivery.attemptedAt)} ·{" "}
                      {delivery.recipientEmails.length === 0
                        ? "No recipients"
                        : delivery.recipientEmails.join(", ")}
                    </p>
                    {delivery.error !== null && (
                      <p className="mt-1 text-[12px] text-rose-700">{delivery.error}</p>
                    )}
                  </div>
                  <span
                    className={cx(
                      "rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1",
                      delivery.status === "sent"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : delivery.status === "failed"
                          ? "bg-rose-50 text-rose-700 ring-rose-200"
                          : "bg-amber-50 text-amber-700 ring-amber-200",
                    )}
                  >
                    {delivery.status}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </div>
  );
}
